import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { routeBase } from '../api/collections';
import { clearUploadStorage } from '../../features/upload/storageKeys';
import { needsFile, setUploadNavigator, uploadWork, useUpload } from '../upload/store';
import { useSession } from './TopBar';

/**
 * The parts of an upload that must run wherever the person is, not only on
 * the upload page: the browser's "leave this page?" prompt while work would
 * be lost, and the way the store's "ready" toast opens the study. Transfers
 * keep running while the person browses, and the store polls the server for
 * the images wherever they are.
 *
 * Mounted once, above every route (router.tsx), so it is there on the
 * study viewer and the shared pages as well as in the community shell.
 */
export function UploadShell() {
  const nav = useNavigate();
  useEffect(() => setUploadNavigator((path) => nav(`${routeBase()}${path}`)), [nav]);
  // Whose sessions these are. A session is stamped with the account that made
  // it; any that belongs to somebody else (the previous account on a shared
  // tab whose session expired without a sign-out) stops, and everything the
  // tab kept for uploads goes with it. Its transfers are stopped here, not
  // left running with nowhere to report.
  const { data: me } = useSession();
  const owners = useUpload((st) => st.sessions.map((x) => x.owner ?? '-').join(','));
  useEffect(() => {
    if (!me?.userId || !owners) return;
    const { sessions, patch, dropAll } = useUpload.getState();
    if (sessions.some((x) => x.owner && x.owner !== me.userId)) {
      dropAll();
      clearUploadStorage();
      return;
    }
    for (const x of sessions) if (!x.owner) patch(x.id, { owner: me.userId });
  }, [me?.userId, owners]);
  // Files the browser kept for transfers that were cut off (a reload, a closed
  // or crashed tab) are opened again wherever the person is, not only on the
  // upload page: a reload of any page carries on. Those the browser must ask
  // about wait for the Resume click on the upload page (store.ts reopenKept).
  const waiting = useUpload((st) => st.sessions.flatMap((x) => x.files).filter((f) => needsFile(f) && f.imageId).map((f) => `${f.imageId}${f.error ? '!' : ''}`).join(','));
  useEffect(() => {
    void useUpload.getState().reopenKept(false);
  }, [waiting]);
  // Armed while a reload would lose something: bytes on the way (a reload
  // makes the person select the files again), or details typed and not yet
  // saved. Everything else is on the server already.
  const armed = useUpload((st) => st.sessions.some((x) => uploadWork(x) === 'transferring' || x.files.some((f) => f.dirty)));
  useEffect(() => {
    if (!armed) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [armed]);
  return null;
}
